// components/social/SocialProvider.jsx
//
// Everything the Custom page needs for social sharing and import, for Super users:
// connected accounts, the last-used platform (one-click share), the worker's status, the open
// tasks (shown as a small tray), and the live view whenever a task needs the person.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  PLATFORMS, cancelTask, defaultCaption, platformMeta, queueTask, saveSettings,
  watchAccounts, watchOpenTasks, watchSettings, watchTask, watchWorker,
} from '../../services/socialService';
import { getCurrentUserEnv } from '../../services/firebase';
import LiveView from './LiveView';
import './social.css';

const SocialContext = createContext(null);
export const useSocial = () => useContext(SocialContext);

const KIND_LABEL = { post: 'Posting', connect: 'Connecting', import: 'Importing', index: 'Indexing', disconnect: 'Disconnecting' };

export default function SocialProvider({ user, enabled, onToast, children }) {
  const uid = user?.uid;
  const [accounts, setAccounts] = useState({});
  const [settings, setSettings] = useState({});
  const [worker, setWorker] = useState({ online: false });
  const [openTasks, setOpenTasks] = useState([]);
  const [finished, setFinished] = useState([]); // recently finished tasks, shown briefly in the tray
  const [liveSession, setLiveSession] = useState(null);
  const dismissedSessions = useRef(new Set());
  const watched = useRef(new Map());
  const pendingAfterConnect = useRef(new Map()); // connect taskId → () => share

  useEffect(() => {
    if (!enabled || !uid) return undefined;
    const subs = [
      watchAccounts(uid, setAccounts),
      watchSettings(uid, setSettings),
      watchWorker(setWorker),
      watchOpenTasks(uid, setOpenTasks),
    ];
    return () => subs.forEach((u) => u && u());
  }, [enabled, uid]);

  // Follow each open task to its end (the open-tasks query drops it the moment it finishes).
  useEffect(() => {
    for (const t of openTasks) {
      if (watched.current.has(t.id)) continue;
      const unsub = watchTask(t.id, (task) => {
        if (!task) return;
        if (['done', 'failed', 'cancelled'].includes(task.status)) {
          unsub();
          watched.current.delete(task.id);
          setFinished((f) => [task, ...f.filter((x) => x.id !== task.id)].slice(0, 4));
          setTimeout(() => setFinished((f) => f.filter((x) => x.id !== task.id)), 9000);
          const meta = platformMeta(task.platform);
          if (task.status === 'done') {
            if (task.kind === 'post') onToast?.(`Posted to ${meta?.label || task.platform}`);
            if (task.kind === 'connect' && task.result?.connected) {
              onToast?.(`${meta?.label} connected${task.result.accountName ? `: ${task.result.accountName}` : ''}`);
              const next = pendingAfterConnect.current.get(task.id);
              pendingAfterConnect.current.delete(task.id);
              if (next) next();
            }
          } else if (task.status === 'failed') {
            onToast?.(`${KIND_LABEL[task.kind] || 'Task'} failed: ${task.error || task.message || 'unknown error'}`);
          }
        }
      });
      watched.current.set(t.id, unsub);
    }
  }, [openTasks, onToast]);
  useEffect(() => () => { watched.current.forEach((u) => u()); watched.current.clear(); }, []);

  // A task waiting for the person opens the live view.
  useEffect(() => {
    const waitingTask = openTasks.find((t) => t.status === 'live' && t.sessionId && !dismissedSessions.current.has(t.sessionId));
    if (waitingTask && liveSession?.id !== waitingTask.sessionId) setLiveSession({ id: waitingTask.sessionId, taskId: waitingTask.id });
  }, [openTasks, liveSession]);

  const isProd = getCurrentUserEnv() !== 'Test';

  const connect = useCallback(async (platform, after) => {
    if (!uid) return null;
    const id = await queueTask(uid, 'connect', platform);
    if (after) pendingAfterConnect.current.set(id, after);
    return id;
  }, [uid]);

  /**
   * Shares a Custom profile. Without a platform it uses the last one used (one click). Not yet
   * connected → connects first (live view), then posts.
   */
  const share = useCallback(async (profile, { platform, caption } = {}) => {
    if (!uid || !profile?.id) return null;
    if (!isProd) { onToast?.('Social sharing works in the Prod environment only.'); return null; }
    const target = platform || settings.lastPlatform || 'instagram';
    const text = caption ?? defaultCaption(profile);
    const run = () => queueTask(uid, 'post', target, { profileId: profile.id, caption: text });
    await saveSettings(uid, { lastPlatform: target }).catch(() => undefined);
    if (!accounts[target]?.connected) {
      onToast?.(`Connect ${platformMeta(target)?.label} once — the post goes out right after.`);
      return connect(target, run);
    }
    const id = await run();
    if (!worker.online) onToast?.('The social worker is offline — the post will go out when it is back.');
    return id;
  }, [uid, isProd, settings.lastPlatform, accounts, worker.online, connect, onToast]);

  const importPosts = useCallback(async (platform, limit) => {
    if (!uid) return null;
    await saveSettings(uid, { lastImportPlatform: platform }).catch(() => undefined);
    return queueTask(uid, 'import', platform, { limit });
  }, [uid]);

  const disconnect = useCallback((platform) => uid && queueTask(uid, 'disconnect', platform), [uid]);

  const busyProfiles = useMemo(() => new Set(openTasks.filter((t) => t.kind === 'post').map((t) => t.payload?.profileId)), [openTasks]);

  const value = useMemo(() => ({
    enabled: enabled && !!uid, uid, accounts, settings, worker, openTasks, busyProfiles, isProd,
    lastPlatform: settings.lastPlatform || 'instagram',
    lastImportPlatform: settings.lastImportPlatform || settings.lastPlatform
      || PLATFORMS.find((p) => accounts[p.id]?.connected)?.id || 'instagram',
    share, connect, disconnect, importPosts, cancelTask,
    openLive: (sessionId) => { dismissedSessions.current.delete(sessionId); setLiveSession({ id: sessionId }); },
  }), [enabled, uid, accounts, settings, worker, openTasks, busyProfiles, isProd, share, connect, disconnect, importPosts]);

  const tray = [...openTasks, ...finished.filter((f) => !openTasks.some((t) => t.id === f.id))];

  return (
    <SocialContext.Provider value={value}>
      {children}
      {enabled && tray.length > 0 && (
        <div className="social-tray" aria-live="polite">
          {tray.map((t) => {
            const meta = platformMeta(t.platform);
            const done = t.status === 'done';
            const failed = t.status === 'failed' || t.status === 'cancelled';
            return (
              <div key={t.id} className={`social-tray-item${done ? ' is-done' : ''}${failed ? ' is-failed' : ''}`}>
                <span className="social-live-badge" style={{ background: meta?.color || '#888' }}>{meta?.short || '★'}</span>
                <div className="social-tray-text">
                  <strong>{KIND_LABEL[t.kind] || t.kind}{meta ? ` · ${meta.label}` : ''}</strong>
                  <span>{failed ? (t.error || t.message) : (t.message || t.status)}</span>
                  {t.kind === 'import' && t.progress?.total > 0 && !done && (
                    <progress max={t.progress.total} value={t.progress.done} />
                  )}
                </div>
                {t.status === 'live' && t.sessionId && (
                  <button type="button" className="social-btn social-btn--primary" onClick={() => value.openLive(t.sessionId)}>Open</button>
                )}
                {t.status === 'queued' && (
                  <button type="button" className="social-btn social-btn--quiet" onClick={() => cancelTask(t.id)}>Cancel</button>
                )}
              </div>
            );
          })}
          {!worker.online && openTasks.some((t) => t.status === 'queued') && (
            <div className="social-tray-note">The social worker looks offline (OneAdmin VPS). Tasks wait until it is back.</div>
          )}
        </div>
      )}
      {liveSession && uid && (
        <LiveView
          key={liveSession.id}
          sessionId={liveSession.id}
          uid={uid}
          onClose={(status, message) => {
            dismissedSessions.current.add(liveSession.id);
            setLiveSession(null);
            if (message && status !== 'closed') onToast?.(message);
          }}
        />
      )}
    </SocialContext.Provider>
  );
}
